/* eslint-disable max-lines */
import { isNil, isNumber } from 'lodash';
import moment from 'moment-timezone';
import { DURATION_UNITS, DurationRegexp, durationUnitDuration } from './constants';
export const relativeDate = (value) => ({
    type: 'relative',
    value,
});
export const absoluteDate = (value) => ({
    type: 'absolute',
    value: value.valueOf(),
});
export const isAbsoluteDate = value => moment.isMoment(value) || isNumber(value) || moment.isDate(value);
export const isRelativeDate = (value) => {
    if (typeof value !== 'string') {
        return false;
    }
    if (value.includes('/')) {
        const relativeDatePattern = /^now([+-]\d+[smhdwMy])?(\/[smhdwMy])?((?:[+-]\d+[smhdwMy])*)$/;
        return relativeDatePattern.test(value);
    }
    const relativeDatePattern = /^now$|^-\d+[smhdwMy]$|^now-\d+[smhdwMy]$/;
    return relativeDatePattern.test(value);
};
export const getTimeRangeVal = (value) => isRelativeDate(value) ? relativeDate(value) : absoluteDate(value);
export const getTimeRange = (start, end) => ({
    start: getTimeRangeVal(start),
    end: getTimeRangeVal(end),
});
export const elasticToMoment = (elasticTime, timezone) => {
    const now = timezone ? moment.tz(timezone) : moment();
    if (elasticTime.startsWith('now')) {
        let result = now.clone();
        const regex = /now([+-]\d+[smhdwMy])?(\/[smhdwMy])?((?:[+-]\d+[smhdwMy])+)?/;
        const match = elasticTime.match(regex);
        if (match) {
            const [, offset, round, additionalOffsets] = match;
            if (offset) {
                const amount = parseInt(offset.slice(1), 10);
                const unit = offset.slice(-1);
                if (unit === 's' || unit === 'm' || unit === 'h' || unit === 'd' || unit === 'w') {
                    result = offset.startsWith('+') ? result.add(amount, unit) : result.subtract(amount, unit);
                }
            }
            if (round) {
                const unit = round.slice(1);
                if (unit === 's' || unit === 'm' || unit === 'h' || unit === 'd' || unit === 'w') {
                    result = result.startOf(unit);
                }
            }
            if (additionalOffsets) {
                const additionalMatches = additionalOffsets.matchAll(/([+-]\d+[smhdwMy])/g);
                for (const additionalOffset of Array.from(additionalMatches)) {
                    const offsetStr = additionalOffset[0];
                    const amount = parseInt(offsetStr.slice(1), 10);
                    const unit = offsetStr.slice(-1);
                    if (unit === 's' || unit === 'm' || unit === 'h' || unit === 'd' || unit === 'w') {
                        result = offsetStr.startsWith('+') ? result.add(amount, unit) : result.subtract(amount, unit);
                    }
                }
            }
            return result;
        }
        throw new Error('Invalid elastic time format');
    }
    else {
        throw new Error('Invalid elastic time format');
    }
};
const isElasticValue = (value) => {
    return typeof value === 'string' && value.includes('/');
};
export const isDurationUnit = (value) => DURATION_UNITS.includes(value);
export const relativeToMoment = (value, timezone) => {
    if (value === 'now') {
        return moment();
    }
    if (isElasticValue(value)) {
        return elasticToMoment(value, timezone);
    }
    const match = value.match(/-?(\d+)(\w)$/);
    if (!match) {
        return moment();
    }
    const [, amount, unit] = match;
    if (isDurationUnit(unit)) {
        if (!isNil(timezone)) {
            return moment.tz(timezone).subtract(Number(amount), unit);
        }
        return moment().subtract(Number(amount), unit);
    }
    return moment();
};
export const dateToUnix = (value) => {
    const isDateUix = new Date(value).getFullYear() === 1970;
    return !isDateUix ? moment(value).unix() : value;
};
export const elasticToAbsolute = (value) => {
    if (typeof value === 'number') {
        const isUnixTimestamp = new Date(value).getFullYear() === 1970;
        return isUnixTimestamp ? value : moment(value).unix();
    }
    return elasticToMoment(value).unix();
};
export const dateToAbsolute = (value) => {
    if (value === null || value === undefined) {
        return 0;
    }
    if (typeof value === 'number') {
        const isUnixTimestamp = new Date(value).getFullYear() === 1970;
        return isUnixTimestamp ? value : moment(value).unix();
    }
    return relativeToMoment(value).unix();
};
export const dateToMoment = (value) => {
    return moment(dateToAbsolute(value) * 1000);
};
/**
 * Resolve a RELATIVE time bound to epoch-ms as of now; `undefined` for anything already absolute (which needs no
 * resolving) or absent. Used to show a relative expression alongside the window it currently means.
 */
export const resolveRelativeBound = (value) => typeof value === 'string' && isRelativeDate(value) ? dateToMoment(value).valueOf() : undefined;
export const dateRangeToMomentTuple = ({ start, end }) => {
    return [
        start.type === 'absolute' ? moment(start.value) : relativeToMoment(start.value),
        end.type === 'absolute' ? moment(end.value) : relativeToMoment(end.value),
    ];
};
export const dateTupleToAbsoluteRange = ([startDate, endDate]) => ({
    start: { type: 'absolute', value: moment(startDate).valueOf() },
    end: { type: 'absolute', value: moment(endDate).valueOf() },
});
export const durationStringToTuple = (duration) => {
    const [, amountStr, unit] = duration.match(/(\d*)(\w*)$/) ?? [];
    const amount = Number(amountStr);
    if (!isNaN(amount) && isDurationUnit(unit)) {
        return [amount, unit];
    }
    return null;
};
export const isDurationString = (value) => {
    return Boolean(value && Boolean(durationStringToTuple(value)));
};
export const durationToText = (duration) => {
    const text = {
        '1h': 'An hour ago',
        '1d': 'A day before',
        '1w': 'A week before',
        '1M': 'A month before',
    }[duration];
    if (text)
        return text;
    const [amount, unit] = durationStringToTuple(duration) ?? [];
    if (!amount || !unit)
        return '';
    const unitText = {
        ms: 'millisecond',
        s: 'second',
        m: 'minute',
        h: 'hour',
        d: 'day',
        w: 'week',
        M: 'month',
        y: 'year',
    }[unit];
    return `${amount} ${unitText}${amount > 1 ? 's' : ''} ago`;
};
export const timeAgo = (dateString) => {
    const now = new Date().getTime();
    const past = new Date(dateString).getTime();
    const diffInMs = now - past;
    const seconds = Math.floor(diffInMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (seconds < 60) {
        return `${seconds}s`;
    }
    else if (minutes < 60) {
        return `${minutes}m`;
    }
    else if (hours < 24) {
        return `${hours}h`;
    }
    return `${days}d`;
};
export const isMoreThanTimeAgo = ({ date, timeInMinutes }) => {
    return moment().diff(moment(date), 'minutes') > timeInMinutes;
};
export const relativeToAbsoluteRange = ({ start, end }) => {
    return {
        start: start.type === 'relative' ? { type: 'absolute', value: relativeToMoment(start.value).valueOf() } : start,
        end: end.type === 'relative' ? { type: 'absolute', value: relativeToMoment(end.value).valueOf() } : end,
    };
};
export const durationToMilliseconds = (duration) => {
    const match = duration.match(DurationRegexp);
    if (match && match[1] && match[2]) {
        const amount = Number(match[1]);
        const unit = match[2];
        return amount * durationUnitDuration[unit];
    }
    return undefined;
};
export const isRelativeRange = (value) => {
    return value?.start?.type === 'relative' && value?.end?.type === 'relative';
};
export const isAbsoluteRange = (value) => {
    return value?.start?.type === 'absolute' && value?.end?.type === 'absolute';
};
export const hasAbsoluteInRange = (value) => {
    return value?.start?.type === 'absolute' || value?.end?.type === 'absolute';
};
export const formatHumanReadableRange = (rangeMs) => {
    if (rangeMs < 1000) {
        return `${rangeMs}ms`;
    }
    const seconds = Math.floor(rangeMs / 1000);
    if (seconds < 60) {
        return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d`;
};
//# sourceMappingURL=date-manipulator.service.js.map